import { createSignal } from 'solid-js';
import { produce } from 'solid-js/store';
import type { ObjectContext } from '../../project/objects';
import type { SelectionContext } from '../selection';
import type { CanvasContext } from '../canvas';
import type { DragContext } from '../drag';
import type { HistoryContext } from '../../project/history';
import { screenToSVG, walkObjects } from '../utils';
import { removeObjectById, setImageUploadInStore } from 'Shared/posters/project/objects';
import type { UploadContext } from './upload';
import { useUser } from 'Shared/SiteProvider';
import type { RuntimeObject, RuntimeImage, UploadedImageData } from '../../types';
import type { ImageImpl } from './ImageImpl';

export class ImageDropper
{
	private objCtx: ObjectContext;
	private selCtx: SelectionContext;
	private canvasCtx: CanvasContext;
	private dragCtx: DragContext;
	private histCtx: HistoryContext;
	private uploadCtx: UploadContext | undefined;
	private userCtx = useUser();

//XXX should dragCounter be in a signal? THINK we can even combine with isDragOver
	private dragCounter = 0;
	readonly isDragOver;
	readonly setIsDragOver;

	constructor(objCtx: ObjectContext, selCtx: SelectionContext, canvasCtx: CanvasContext, dragCtx: DragContext, histCtx: HistoryContext, uploadCtx: UploadContext | undefined)
	{
		this.objCtx = objCtx;
		this.selCtx = selCtx;
		this.canvasCtx = canvasCtx;
		this.dragCtx = dragCtx;
		this.histCtx = histCtx;
		this.uploadCtx = uploadCtx;
		[this.isDragOver, this.setIsDragOver] = createSignal(false);
	}

	isImageDrag(e: DragEvent): boolean 
	{
		const types = [...(e.dataTransfer?.types ?? [])]
		return types.includes('Files') || types.includes('text/uri-list')
	}

	findTopmostImageAt(svgX: number, svgY: number): RuntimeImage | null 
	{
		let found: RuntimeImage | null = null
		walkObjects(this.objCtx.objects(), obj => {
			if (obj.type !== 'image') 
				return false;
			const el = this.selCtx.impls[obj.id];
			if (!el?.getBBox) 
				return false;
			const { pos: { x, y }, dims: { width: w, height: h } } = el.getBBox(!!this.userCtx);
			if (svgX >= x && svgX <= x + w && svgY >= y && svgY <= y + h) 
				found = obj as RuntimeImage;
			return false;
		})
		return found;
	}

	onDragEnter(e: DragEvent): void 
	{
		if (!this.isImageDrag(e)) 
			return;
		e.preventDefault();
		this.dragCounter++;
		this.setIsDragOver(true);
	}

	onDragOver(e: DragEvent): void 
	{
		if (!this.isImageDrag(e)) 
			return;
		e.preventDefault();
		if (e.dataTransfer) 
			e.dataTransfer.dropEffect = 'copy';
	}

	onDragLeave(): void 
	{
		if (--this.dragCounter <= 0) { 
			this.dragCounter = 0; 
			this.setIsDragOver(false);
		}
	}

	async onDrop(e: DragEvent): Promise<void> 
	{
		e.preventDefault()
		this.dragCounter = 0
		this.setIsDragOver(false)

		const svg = this.dragCtx.svgRef()
		if (!svg) return
		const { width: W, height: H } = this.canvasCtx.canvas()
		const svgPt = screenToSVG(svg, { x: e.clientX, y: e.clientY })
		if (svgPt.x < 0 || svgPt.y < 0 || svgPt.x > W || svgPt.y > H) return

		// upload holds structured data for server-uploaded files; fallbackSrc holds
		// a plain URL (data URL or URI-list drop) when no upload context is available.
		let upload: UploadedImageData | null = null
		let fallbackSrc: string | null = null

		const dt = e.dataTransfer
		const imageFile = [...(dt?.files ?? [])].find(f => f.type.startsWith('image/'))
		if (imageFile) {
			if (this.uploadCtx) {
				upload = await this.uploadCtx.uploadImage(imageFile)
			} else {
				// Fallback for non-server contexts: store as data URL
				fallbackSrc = await new Promise<string>(resolve => {
					const reader = new FileReader()
					reader.onload = ev => resolve((ev.target as FileReader).result as string)
					reader.readAsDataURL(imageFile)
				})
			}
		} else {
			const uri = dt?.getData('text/uri-list')
				?.split('\n').map(s => s.trim()).find(s => s && !s.startsWith('#'))
			if (uri) fallbackSrc = uri
		}
		if (!upload && !fallbackSrc) return

		const hitObj = this.findTopmostImageAt(svgPt.x, svgPt.y)

		if (hitObj && !hitObj.isField) {
			if (upload) {
				this.histCtx.execute({
					label: `image-upload ${hitObj.id} (drop replace)`,
					execute: () => {
						setImageUploadInStore(this.objCtx, hitObj.id, upload)
						this.selCtx.select(hitObj.id)
					},
					undo: () => {
						setImageUploadInStore(this.objCtx, hitObj.id, hitObj.image ?? null)
					},
				})
			} else {
				(this.selCtx.impls[hitObj.id] as ImageImpl | undefined)?.setImageSrc(fallbackSrc, this.objCtx)
				this.selCtx.select(hitObj.id)
			}

		} else if (hitObj?.isField) {
			const fieldId = hitObj.id
			const liveBox = this.selCtx.impls[fieldId]?.getBBox?.()
			const pos  = liveBox
				? { x: Math.round(liveBox.pos.x), y: Math.round(liveBox.pos.y) }
				: { ...(hitObj.position ?? { x: 0, y: 0 }) }
			const size = liveBox
				? { width: Math.round(liveBox.dims.width), height: Math.round(liveBox.dims.height) }
				: { ...(hitObj.size ?? { width: 160, height: 120 }) }
			const snapshot = JSON.parse(JSON.stringify(hitObj)) as RuntimeImage
			const src = fallbackSrc  // field images always use fallback URL path for now
			const converted: RuntimeImage = { type: 'image', id: fieldId, src: src ?? undefined, position: pos, size }
			if (upload) 
				converted.image = upload;

			this.histCtx.execute({
				execute: () => {
					this.objCtx.setObjects(produce((arr: RuntimeObject[]) => {
						const i = arr.findIndex(o => o.id === fieldId)
						if (i >= 0) arr.splice(i, 1, { ...converted })
					}));
					this.selCtx.select(fieldId);
				},
				undo: () => {
					this.objCtx.setObjects(produce((arr: RuntimeObject[]) => {
						const i = arr.findIndex(o => o.id === fieldId)
						if (i >= 0) arr.splice(i, 1, { ...snapshot })
					}));
					this.selCtx.select(fieldId);
				},
			})

		} else {
			const objW = 160, objH = 120;
			const left = Math.max(0, Math.min(Math.round(svgPt.x - objW / 2), W - objW));
			const top  = Math.max(0, Math.min(Math.round(svgPt.y - objH / 2), H - objH));
			const id = `image-${Date.now()}`;
			const newObj: RuntimeImage = {
				type: 'image', id,
				src:     fallbackSrc ?? undefined,
				image: upload ?? undefined,
				position: { x: left, y: top },
				size: { width: objW, height: objH },
				isField: false,
			}

			this.histCtx.execute({
				execute: () => {
					this.objCtx.setObjects(produce((arr: RuntimeObject[]) => arr.push({ ...newObj })));
					this.selCtx.select(id);
				},
				undo: () => {
					removeObjectById(this.objCtx, id);
					this.selCtx.deselect();
				},
			})
		}
	}
}
